import { ActivitySeries } from "@common/schemas/domains/activity/activity-timeline.schema";
import { ActorKind, SourcePredicate } from "@common/types/auditLog";
import {
    ListedSeriesParams,
    toListedSeries,
} from "@components/activityTimeline/activityTimelineSeriesFilter";
import type { HistoryFilters } from "@hooks/view/useHistoryPageFiltersCache";
import {
    arePredicatesSubsetOf,
    getSourcePredicatesForActor,
    withoutPredicates,
} from "../utils/actorPredicates";

/** An Updated by row that picks one of the Activity timeline's series. */
export interface SeriesFilterItem {
    type: "series";
    series: ActivitySeries;
}

const toSeriesFilterItem = (series: ActivitySeries): SeriesFilterItem => ({
    type: "series",
    series,
});

/** The series the workspace Updated by picks with the Activity timeline. */
export const WORKSPACE_PICKABLE_SERIES: readonly ActivitySeries[] = [
    ActivitySeries.RULES,
    ActivitySeries.KICK,
    ActivitySeries.INTEGRATIONS,
];

/** The series rows an Updated by lists, as picker items. */
export const toListedSeriesFilterItems = (
    params: ListedSeriesParams,
): SeriesFilterItem[] => toListedSeries(params).map(toSeriesFilterItem);

/** Kick and Rule picks saved before the series filter; they still filter by source. */
const SAVED_PREDICATE_KINDS: Partial<Record<ActivitySeries, ActorKind>> = {
    [ActivitySeries.KICK]: ActorKind.KICK,
    [ActivitySeries.RULES]: ActorKind.RULE,
};

const SAVED_PREDICATES = Object.values(SAVED_PREDICATE_KINDS).flatMap((kind) =>
    kind === undefined ? [] : getSourcePredicatesForActor({ kind }),
);

/** The picked integrations' `sourcePredicates`, without saved Kick and Rule picks. */
export const toIntegrationPredicates = (
    sourcePredicates: SourcePredicate[] | undefined,
): SourcePredicate[] => withoutPredicates(sourcePredicates, SAVED_PREDICATES);

/** Whether Updated by picks the whole series, or its saved Kick or Rule pick. */
export function isSeriesPicked({
    series,
    filters,
}: {
    series: ActivitySeries;
    filters: Pick<HistoryFilters, "series" | "sourcePredicates">;
}): boolean {
    if (filters.series?.includes(series)) {
        return true;
    }
    const savedKind = SAVED_PREDICATE_KINDS[series];
    return (
        savedKind !== undefined &&
        arePredicatesSubsetOf(
            getSourcePredicatesForActor({ kind: savedKind }),
            filters.sourcePredicates,
        )
    );
}
